/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGlobalSearch, useProcess } from "@/global-data";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useToast } from "@/hooks/use-toast";
import { getUserInfo } from "@/lib/token";
import { getCurrentTimeZone } from "@/lib/utils";
import { wait } from "@/lib/wait";
import { apiServices } from "@/services/api.services";
import { email_read_logs } from "@/services/global.services";
import { useEffect, useMemo, useState } from "react";

const SAVED_STATE_KEY = "email_read_logs_saved_state";
const RETURN_FLAG_KEY = "email_read_logs_return_from_stats";

export const useMutations = () => {
  const API_SERVICE = email_read_logs;
  const { company_id, user_id } = useMemo(() => {
    const tempUserData = getUserInfo();
    return {
      company_id: tempUserData?.company_id,
      user_id: tempUserData?.user_id || "",
    };
  }, []);
  const timeZone = getCurrentTimeZone();

  const { setProcess } = useProcess();
  const [skeltonLoading, setSkeltonLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [tableColumn, setTableColumn] = useState([]);

  const { setSearchProgress, search } = useGlobalSearch();
  const { toast } = useToast();
  const [searchDebounced] = useDebouncedValue(search, 200);
  const [filter, setFilter] = useState<any>({});

  const [paginationData, setPaginationData] = useState({
    page: 1,
    perPage: 10,
    totalItem: 1,
  });

  // Save filter + pagination to sessionStorage on every change
  useEffect(() => {
    if (!skeltonLoading) {
      sessionStorage.setItem(
        SAVED_STATE_KEY,
        JSON.stringify({
          filter,
          page: paginationData.page,
          perPage: paginationData.perPage,
        })
      );
    }
  }, [filter, paginationData.page, paginationData.perPage, skeltonLoading]);

  //On Load Component — restore state if returning from stats, otherwise fresh load
  useEffect(() => {
    (async () => {
      const returnFlag = sessionStorage.getItem(RETURN_FLAG_KEY);

      if (returnFlag) {
        sessionStorage.removeItem(RETURN_FLAG_KEY);
        const saved = sessionStorage.getItem(SAVED_STATE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          const restoredFilter = parsed.filter || {};
          const restoredPage = parsed.page || 1;
          const restoredPerPage = parsed.perPage || 10;
          setFilter(restoredFilter);
          setPaginationData((prev) => ({
            ...prev,
            page: restoredPage,
            perPage: restoredPerPage,
          }));
          await loadingList({
            page: restoredPage,
            perPage: restoredPerPage,
            filterOverride: restoredFilter,
          });
        } else {
          await loadingList({ searchText: "" });
        }
      } else {
        sessionStorage.removeItem(SAVED_STATE_KEY);
        await loadingList({ searchText: "" });
      }

      await wait(1000);
      setSkeltonLoading(false);
      setProcess(false);
    })();
  }, []);

  //Handle Search
  useEffect(() => {
    if (skeltonLoading === false) {
      (async () => {
        setSearchProgress(true);
        await loadingList({});
        setSearchProgress(false);
      })();
    }
  }, [searchDebounced]);

  const onPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, page: prop }));
    setProcess(true);
    await loadingList({ page: prop });
    setProcess(false);
  };
  const onPerPageChange = async (prop: number) => {
    setPaginationData((prev) => ({ ...prev, perPage: prop, page: 1 }));
    setProcess(true);
    await loadingList({ perPage: prop, page: 1 });
    setProcess(false);
  };

  const permission: any = [];

  const loadingList = async ({
    perPage,
    page,
    filterReset,
    makeFilter,
    searchText,
    filterOverride,
  }: {
    perPage?: number;
    page?: number;
    filterReset?: boolean;
    makeFilter?: boolean;
    searchText?: string;
    filterOverride?: any;
  }) => {
    let apiFilter: any = {};

    if (filterOverride !== undefined) {
      apiFilter = filterOverride;
    } else if (!filterReset) {
      apiFilter = filter;
    }

    let page_no = page || paginationData.page;

    if (makeFilter) {
      page_no = 1;
    }

    const payload = {
      data: {
        filter_by: {
          ...apiFilter,
          search_text: searchText ?? searchDebounced,
          exact_match: false,
          pagination: {
            page_no: page_no,
            no_of_recs: perPage || paginationData.perPage,
            sort_by: "cts",
            order_by: false,
          },
        },
        email_id: "mindmastertest64@gmail.com",
      },
      company_id: company_id,
      user_id: user_id,
      timezone: timeZone,
    };

    const { data, error, message } =
      await apiServices<any>(API_SERVICE.get, "post", payload);

    if (data) {
      const listData = data.result.metadata?.[API_SERVICE.key];
      setListData(listData);

      setPaginationData((prev) => ({
        ...prev,
        totalItem: data?.result?.metadata?.total_records || 1,
      }));
    }

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }

    return { data, error };
  };

  const filterRequest = async () => {
    setProcess(true);
    await loadingList({ makeFilter: true });
    setProcess(false);
  };
  const clearFilter = async () => {
    setFilter({});
    setProcess(true);
    await loadingList({ filterReset: true });
    setProcess(false);
  };

  return {
    skeltonLoading,
    listData,
    onPageChange,
    onPerPageChange,
    paginationData,
    permission,

    tableColumn,
    setTableColumn,
    filter,
    setFilter,
    filterRequest,
    clearFilter,
  };
};
